/**
 * The till with no connection.
 *
 * A smaller till than the online one, on purpose: what a restaurant needs to
 * keep serving while the line is down, and nothing that needs the server to
 * be right. Pick a table or none, add dishes from the menu this tablet last
 * saw, send the kitchen its ticket on the printer, take cash or a card on the
 * card machine, and hand over a receipt with a real number. Each sale is kept
 * on the tablet and sent when the connection returns (`sync.ts`).
 *
 * Tabs opened online before the line dropped are on the server, and are not
 * here: they carry on when it comes back.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  TENDER_LABELS,
  formatMoney,
  groupCategories,
  readerForThisTill,
  setDisplayFormat,
  type MenuItem,
  type TenderType,
} from '@restaurant/api-client';
import {
  Button,
  Empty,
  ErrorNotice,
  Field,
  Input,
  NumberPad,
  Segmented,
  Sheet,
  receiptLines,
} from '@restaurant/ui';

import { kickDrawer } from '../printer';
import { printLines, printWidth } from './print';
import {
  decimal,
  describeLine,
  kitchenTicket,
  lineCents,
  receiptPayload,
  saleForServer,
  totalCents,
  unitCents,
  type CartLine,
  type PaymentTaken,
} from './sale';
import { enqueue, loadNumbers, numbersLeft, takeNumber, type Snapshot } from './store';
import { useQueue } from './sync';

const money = (value: number) => formatMoney(decimal(value));

function newId(): string {
  return crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

export function OfflineTill({ snapshot }: { snapshot: Snapshot }) {
  setDisplayFormat({ currency: snapshot.location.currency, locale: snapshot.location.locale });
  const queue = useQueue();
  const [left, setLeft] = useState<number | null>(null);
  const [lines, setLines] = useState<CartLine[]>([]);
  const [tableId, setTableId] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [choosing, setChoosing] = useState<MenuItem | null>(null);
  const [paying, setPaying] = useState(false);
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const [sentToKitchen, setSentToKitchen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    void loadNumbers().then((n) =>
      setLeft(n && n.terminalId === snapshot.terminalId ? numbersLeft(n) : 0),
    );
  }, [snapshot.terminalId, queue.length]);

  const items = snapshot.menu.items.filter((item) => item.is_active && item.is_available);
  // A group ("Drinks") shows everything in the categories inside it too: one
  // row of buttons is enough for the hours a till spends offline.
  const sections = groupCategories(snapshot.menu.categories)
    .map((group) => ({
      id: group.category.id,
      name: group.category.name,
      within: [group.category.id, ...group.inside.map((c) => c.id)],
    }))
    .filter((s) => items.some((item) => s.within.includes(item.category_id)));
  const section = sections.find((s) => s.id === category) ?? sections[0];
  const shown = section
    ? items
        .filter((item) => section.within.includes(item.category_id))
        .sort(
          (a, b) =>
            section.within.indexOf(a.category_id) - section.within.indexOf(b.category_id),
        )
    : [];
  const table = snapshot.tables.find((t) => t.id === tableId) ?? null;
  const total = totalCents(lines);

  if (!snapshot.terminalId) {
    return (
      <OfflineFrame snapshot={snapshot} waiting={queue.length} left={left}>
        <Empty
          title="This tablet is not set as a till"
          description="Only a till can sell with no connection, because it needs receipt numbers of its own. When the line is back, choose this tablet's till under This tablet → Cash drawer."
        />
      </OfflineFrame>
    );
  }

  function add(item: MenuItem, variantId: string | null, optionIds: string[]) {
    if (!openedAt) setOpenedAt(new Date().toISOString());
    setLines((current) => {
      const same = current.find(
        (l) =>
          l.item.id === item.id &&
          l.variantId === variantId &&
          l.optionIds.join() === optionIds.join(),
      );
      if (same) return current.map((l) => (l === same ? { ...l, qty: l.qty + 1 } : l));
      return [...current, { key: newId(), item, variantId, optionIds, qty: 1 }];
    });
    setSentToKitchen(false);
  }

  function tap(item: MenuItem) {
    if (item.variants.length > 1 || item.modifier_groups.length > 0) setChoosing(item);
    else add(item, item.variants[0]?.id ?? null, []);
  }

  async function toKitchen() {
    setProblem(null);
    const trouble = await printLines(
      kitchenTicket(lines, {
        table: table?.label ?? null,
        at: new Date(),
        width: printWidth(),
      }),
    );
    if (trouble) setProblem(`The kitchen ticket did not print. ${trouble}`);
    else setSentToKitchen(true);
  }

  async function finish(payment: PaymentTaken) {
    setProblem(null);
    const number = await takeNumber();
    if (number === null) {
      setProblem('This till has no receipt numbers left, so it cannot finish a sale offline.');
      return;
    }
    const closedAt = new Date().toISOString();
    const reference = `R-${String(number).padStart(6, '0')}`;
    const guests = 1;
    const payload = receiptPayload(lines, {
      locationName: snapshot.location.name,
      table: table?.label ?? null,
      guests,
      payment,
      closedAt,
    });
    const clientId = newId();
    // Kept before anything prints: a power cut after the receipt and before
    // this line would lose a sale the guest has paper for.
    await enqueue({
      sale: saleForServer(lines, {
        clientId,
        locationId: snapshot.location.id,
        terminalId: snapshot.terminalId!,
        openedAt: openedAt ?? closedAt,
        closedAt,
        receiptNumber: number,
        tableId: table?.id ?? null,
        guests,
        payment,
      }),
      receipt: { reference, payload, issuedAt: closedAt },
    });
    if (payment.tender === 'cash') void kickDrawer();
    const trouble = await printLines(
      receiptLines({
        reference,
        reprintCount: 0,
        issuedAt: closedAt,
        payload,
        money: (amount) => formatMoney(amount),
        tender: (key) => TENDER_LABELS[key as TenderType] ?? key,
        locale: snapshot.location.locale,
        width: printWidth(),
      }),
    );
    const change = payment.tenderedCents - payment.amountCents;
    setNotice(
      `${reference} kept on this tablet${trouble ? ', but the receipt did not print' : ' and printed'}.` +
        (change > 0 ? ` Change: ${money(change)}.` : ''),
    );
    if (trouble) setProblem(trouble);
    setLines([]);
    setTableId(null);
    setOpenedAt(null);
    setSentToKitchen(false);
    setPaying(false);
  }

  return (
    <OfflineFrame snapshot={snapshot} waiting={queue.length} left={left}>
      <div className="ol">
        <section className="ol__menu" aria-label="Menu">
          <Segmented
            label="Section"
            scroll
            options={sections.map((s) => ({ value: s.id, label: s.name }))}
            value={section?.id ?? null}
            onChange={setCategory}
          />
          <div className="ol__dishes">
            {shown.map((item) => (
              <button
                key={item.id}
                type="button"
                className="ol__dish"
                onClick={() => tap(item)}
              >
                <span className="ol__dish-name">{item.name}</span>
                <span className="ol__dish-price tabular">{formatMoney(item.price)}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="ol__ticket" aria-label="This sale">
          <label className="ol__table">
            <span>Table</span>
            <select value={tableId ?? ''} onChange={(e) => setTableId(e.target.value || null)}>
              <option value="">No table</option>
              {snapshot.tables.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>

          {notice && lines.length === 0 && (
            <p className="ol__notice" role="status">
              {notice}
            </p>
          )}

          {lines.length === 0 ? (
            <p className="ol__empty">Tap a dish to start a sale.</p>
          ) : (
            <ul className="ol__lines">
              {lines.map((line) => {
                const { name, variant, options } = describeLine(line);
                return (
                  <li key={line.key} className="ol__line">
                    <span className="ol__line-name">
                      {name}
                      {variant && ` (${variant})`}
                      {options.length > 0 && (
                        <span className="ol__line-opts">{options.join(', ')}</span>
                      )}
                    </span>
                    <span className="ol__qty">
                      <button
                        type="button"
                        aria-label={`One fewer ${name}`}
                        onClick={() =>
                          setLines((all) =>
                            all
                              .map((l) => (l.key === line.key ? { ...l, qty: l.qty - 1 } : l))
                              .filter((l) => l.qty > 0),
                          )
                        }
                      >
                        −
                      </button>
                      <span className="tabular">{line.qty}</span>
                      <button
                        type="button"
                        aria-label={`One more ${name}`}
                        onClick={() =>
                          setLines((all) =>
                            all.map((l) => (l.key === line.key ? { ...l, qty: l.qty + 1 } : l)),
                          )
                        }
                      >
                        +
                      </button>
                    </span>
                    <span className="ol__line-total tabular">{money(lineCents(line))}</span>
                  </li>
                );
              })}
            </ul>
          )}

          <p className="ol__total">
            <span>Total</span>
            <span className="tabular">{money(total)}</span>
          </p>

          {problem && <ErrorNotice title="Something needs a look" message={problem} />}

          <div className="ol__acts">
            <Button size="lg" disabled={lines.length === 0} onClick={() => void toKitchen()}>
              {sentToKitchen ? 'Ticket printed — print again' : 'Kitchen ticket'}
            </Button>
            <Button
              variant="primary"
              size="lg"
              disabled={lines.length === 0 || left === 0}
              onClick={() => setPaying(true)}
            >
              Pay {money(total)}
            </Button>
          </div>
        </section>
      </div>

      {choosing && (
        <DishSheet
          item={choosing}
          onAdd={(variantId, optionIds) => {
            add(choosing, variantId, optionIds);
            setChoosing(null);
          }}
          onDismiss={() => setChoosing(null)}
        />
      )}
      {paying && (
        <PaySheet
          totalCents={total}
          onPaid={(payment) => void finish(payment)}
          onDismiss={() => setPaying(false)}
        />
      )}
    </OfflineFrame>
  );
}

function OfflineFrame({
  snapshot,
  waiting,
  left,
  children,
}: {
  snapshot: Snapshot;
  waiting: number;
  left: number | null;
  children: React.ReactNode;
}) {
  return (
    <div className="ol-frame">
      <header className="ol-frame__bar" role="status">
        <span className="ol-frame__dot" aria-hidden="true" />
        <span className="ol-frame__what">
          <strong>No connection</strong> — selling on this tablet at {snapshot.location.name}.
        </span>
        <span className="ol-frame__facts">
          {waiting > 0 && <span>{waiting} to send</span>}
          {left !== null && <span>{left} receipt numbers left</span>}
        </span>
      </header>
      {children}
    </div>
  );
}

function DishSheet({
  item,
  onAdd,
  onDismiss,
}: {
  item: MenuItem;
  onAdd: (variantId: string | null, optionIds: string[]) => void;
  onDismiss: () => void;
}) {
  const [variantId, setVariantId] = useState<string | null>(
    item.variants.find((v) => v.is_default)?.id ?? item.variants[0]?.id ?? null,
  );
  const [optionIds, setOptionIds] = useState<string[]>([]);
  const short = item.modifier_groups.find(
    (g) => g.options.filter((o) => optionIds.includes(o.id)).length < g.min_select,
  );
  const price = unitCents({ item, variantId, optionIds });

  function toggle(groupId: string, optionId: string) {
    const group = item.modifier_groups.find((g) => g.id === groupId)!;
    setOptionIds((current) => {
      if (current.includes(optionId)) return current.filter((id) => id !== optionId);
      const inGroup = current.filter((id) => group.options.some((o) => o.id === id));
      // One choice: picking another swaps it.
      if (group.max_select === 1) {
        return [...current.filter((id) => !inGroup.includes(id)), optionId];
      }
      if (group.max_select !== null && inGroup.length >= group.max_select) return current;
      return [...current, optionId];
    });
  }

  return (
    <Sheet
      title={item.name}
      onDismiss={onDismiss}
      actions={
        <Button
          variant="primary"
          size="lg"
          block
          disabled={Boolean(short)}
          onClick={() => onAdd(variantId, optionIds)}
        >
          {short ? `Choose ${short.prompt ?? short.name}` : `Add · ${money(price)}`}
        </Button>
      }
    >
      {item.variants.length > 1 && (
        <Segmented
          label="Size"
          options={item.variants.map((v) => ({ value: v.id, label: v.name }))}
          value={variantId}
          onChange={setVariantId}
        />
      )}
      {item.modifier_groups.map((group) => (
        <fieldset key={group.id} className="ol__group">
          <legend>{group.prompt ?? group.name}</legend>
          <div className="ol__options">
            {group.options
              .filter((o) => o.is_available)
              .map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`ol__option${optionIds.includes(option.id) ? ' is-on' : ''}`}
                  aria-pressed={optionIds.includes(option.id)}
                  onClick={() => toggle(group.id, option.id)}
                >
                  {option.name}
                  {Number(option.price_delta) !== 0 && (
                    <span className="tabular"> +{formatMoney(option.price_delta)}</span>
                  )}
                </button>
              ))}
          </div>
        </fieldset>
      ))}
    </Sheet>
  );
}

function PaySheet({
  totalCents: due,
  onPaid,
  onDismiss,
}: {
  totalCents: number;
  onPaid: (payment: PaymentTaken) => void;
  onDismiss: () => void;
}) {
  const [tender, setTender] = useState<'cash' | 'card'>('cash');
  const [given, setGiven] = useState('');
  const [code, setCode] = useState('');
  const [last4, setLast4] = useState('');
  const givenCents = useMemo(() => Math.round(Number(given || '0') * 100), [given]);
  const short = tender === 'cash' && given !== '' && givenCents < due;
  const at = () => new Date().toISOString();

  // The exact sum and the notes a guest is likely to hand over for it.
  const quick = [
    due,
    ...[500, 1000, 2000, 5000, 10000].map((note) => Math.ceil(due / note) * note),
  ]
    .filter((v, i, all) => v >= due && all.indexOf(v) === i)
    .slice(0, 4);

  return (
    <Sheet title={`Pay ${money(due)}`} onDismiss={onDismiss}>
      <Segmented
        label="How they pay"
        options={[
          { value: 'cash', label: 'Cash' },
          { value: 'card', label: 'Card' },
        ]}
        value={tender}
        onChange={(t) => setTender(t as 'cash' | 'card')}
      />

      {tender === 'cash' ? (
        <>
          <div className="ol__quick">
            {quick.map((value) => (
              <Button key={value} onClick={() => setGiven(decimal(value))}>
                {money(value)}
              </Button>
            ))}
          </div>
          <NumberPad label="Given" value={given} onChange={setGiven} />
          {given !== '' && !short && (
            <p className="ol__change">Change {money(givenCents - due)}</p>
          )}
          <Button
            variant="primary"
            size="lg"
            block
            disabled={short}
            onClick={() =>
              onPaid({
                tender: 'cash',
                amountCents: due,
                tenderedCents: given === '' ? due : givenCents,
                tipCents: 0,
                at: at(),
                cardReaderId: null,
                authCode: null,
                cardLast4: null,
              })
            }
          >
            {short ? 'Not enough given' : 'Cash taken'}
          </Button>
        </>
      ) : (
        <>
          <p className="ol__card">
            Key <strong className="tabular">{money(due)}</strong> on the card machine, then
            press what it shows.
          </p>
          <div className="ol__slip">
            <Field label="Code on the slip" hint="Optional">
              <Input value={code} maxLength={40} onChange={(e) => setCode(e.target.value)} />
            </Field>
            <Field label="Last four digits" hint="Optional">
              <Input
                value={last4}
                inputMode="numeric"
                maxLength={4}
                onChange={(e) => setLast4(e.target.value.replace(/\D/g, ''))}
              />
            </Field>
          </div>
          <div className="ol__card-acts">
            <Button
              variant="primary"
              size="lg"
              onClick={() =>
                onPaid({
                  tender: 'card',
                  amountCents: due,
                  tenderedCents: due,
                  tipCents: 0,
                  at: at(),
                  cardReaderId: readerForThisTill(),
                  authCode: code.trim() || null,
                  cardLast4: last4.length === 4 ? last4 : null,
                })
              }
            >
              Approved
            </Button>
            <Button size="lg" onClick={onDismiss}>
              Declined
            </Button>
          </div>
        </>
      )}
    </Sheet>
  );
}
